<template>
  <PaginaAdministrar
    titulo="Gestão de Jobs (Robôs)"
    subtitulo="Agendamento e monitoramento de rotinas em background"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca (Nome ou Descrição)</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Ex: SYNC_NCM_CADA" @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Último Status</label>
          <select class="form-select" v-model="filtros.statusUltimo" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option value="Sucesso">Sucesso</option>
            <option value="Falha">Falha</option>
            <option value="Em Andamento">Em Processamento</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">A carregar...</span></div>
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum Job configurado no sistema."
      row-key="id"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.id }}</span>
      </template>
      <template #identificacao="{ row }">
        <div class="fw-bold text-body-emphasis">{{ row.nomeJob }}</div>
        <div class="small text-body-secondary text-truncate" style="max-width: 250px;" :title="row.descricao">{{ row.descricao || 'Sem descrição' }}</div>
      </template>

      <template #frequencia="{ row }">
        <span class="font-monospace text-center bg-body-tertiary px-2 py-1 rounded">{{ row.expressaoCron }}</span>
      </template>

      <template #ultimasExecucoes="{ row }">
        <div class="d-flex justify-content-center gap-1 align-items-center h-100">
          <span v-if="!row.ultimosResultados || row.ultimosResultados.length === 0" class="text-muted small">-</span>
          <template v-else>
            <template v-for="(res, idx) in row.ultimosResultados" :key="idx">
              <span v-if="res === 'Em Andamento'" class="spinner-border text-warning" style="width: 1.1rem; height: 1.1rem; border-width: 0.15em;" title="Em Processamento"></span>
              <i v-else class="bi" :class="res === 'Sucesso' ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'" :title="res" style="font-size: 1.1rem;"></i>
            </template>
          </template>
          <span v-if="row.executarImediatamente && (!row.ultimosResultados || row.ultimosResultados[row.ultimosResultados.length - 1] !== 'Em Andamento')" class="spinner-border text-warning ms-1" style="width: 1.1rem; height: 1.1rem; border-width: 0.15em;" title="Em Processamento"></span>
        </div>
      </template>

      <template #proximaExecucao="{ row }">
        <span v-if="!row.isAtivo" class="text-muted">-</span>
        <span v-else-if="row.dataProximaExecucao" class="fw-bold" :class="{'text-danger': isAtrasado(row.dataProximaExecucao)}">
          <i class="bi bi-clock me-1 text-secondary"></i>{{ formatarDataHoraBR(row.dataProximaExecucao) }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #statusGlobal="{ row }">
        <span class="badge" :class="row.isAtivo ? 'bg-success' : 'bg-secondary'">{{ row.isAtivo ? 'Ativo' : 'Inativo' }}</span>
        <div v-if="row.tentativasAtuais > 0" class="text-danger small mt-1 fw-bold">
          <i class="bi bi-exclamation-circle-fill"></i> {{ row.tentativasAtuais }} falha(s)
        </div>
        <div v-if="!row.isAtivo && row.tentativasAtuais > 0" class="text-danger small mt-1 fw-bold">
          (Desativado pelo Motor)
        </div>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-hover-container">
          <button v-if="!row.executarImediatamente" class="btn-icon text-success always-visible" @click="executarAgora(row)" title="Executar Agora" :disabled="!row.isAtivo">
            <i class="bi bi-play-circle-fill"></i>
          </button>
          <button v-if="row.isAtivo || row.executarImediatamente" class="btn-icon text-danger always-visible" @click="pararJob(row)" title="Parar Job">
            <i class="bi bi-stop-circle-fill"></i>
          </button>
          <button class="btn-icon text-secondary btn-hover-only ms-2" @click="abrirHistorico(row)" title="Ver Histórico">
            <i class="bi bi-journal-text"></i>
          </button>
          <button class="btn-icon text-secondary btn-hover-only" @click="abrirAuditoria(row)" title="Ver Auditoria">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon text-primary btn-hover-only" @click="editar(row)" title="Editar Configuração">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <!-- MODAL DE HISTÓRICO DE EXECUÇÕES -->
  <ModalFormulario
    v-model="modalHistoricoAberto"
    :titulo="`Histórico: ${jobSelecionado?.nomeJob}`"
    tamanho="xl"
    :ocultar-salvar="true"
    texto-cancelar="Fechar"
  >
    <div v-if="loadingHistorico" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary" role="status"></div> A carregar histórico...
    </div>
    <div v-else class="admin-table-container border-0 shadow-none m-0">
      <table class="table table-sm admin-table m-0">
        <thead>
          <tr>
            <th>Início (UTC)</th>
            <th>Fim (UTC)</th>
            <th class="text-center">Origem</th>
            <th class="text-center">Tentativa</th>
            <th>Duração</th>
            <th>Registos</th>
            <th>Status</th>
            <th class="text-center">Detalhes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="historicoLogs.length === 0">
            <td colspan="8" class="text-center text-muted py-3">Nenhuma execução registada para este Job.</td>
          </tr>
          <tr v-for="log in historicoLogs" :key="log.id">
            <td>{{ formatarDataHoraBR(log.dataHoraInicio) }}</td>
            <td>{{ formatarDataHoraBR(log.dataHoraFim) }}</td>
            <td class="text-center"><span class="badge bg-body-secondary text-body border">{{ log.tipoExecucao || 'Agendado' }}</span></td>
            <td class="text-center font-monospace">{{ log.tentativa || 1 }}</td>
            <td>{{ log.duracaoSegundos ? log.duracaoSegundos.toFixed(1) + 's' : '-' }}</td>
            <td>{{ log.registrosProcessados }}</td>
            <td><span :class="obterClasseStatusLog(log.status)">{{ log.status }}</span></td>
            <td class="text-center">
               <button v-if="log.mensagemErro || log.detalhesJson" class="btn btn-sm btn-link py-0" @click="verDetalheErro(log)">Ver Detalhes</button>
               <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </ModalFormulario>

  <!-- MODAL PARA VER ERRO COPIÁVEL -->
  <ModalFormulario
    v-model="modalErroAberto"
    titulo="Detalhes do Log"
    tamanho="lg"
    :ocultar-salvar="true"
    texto-cancelar="Fechar"
  >
    <div class="alert alert-warning mb-2 small py-2"><i class="bi bi-info-circle me-1"></i> Pode selecionar e copiar o texto abaixo.</div>
    <pre class="bg-dark text-light p-3 rounded" style="white-space: pre-wrap; word-break: break-all; max-height: 60vh; overflow-y: auto;">{{ logErroSelecionado }}</pre>
  </ModalFormulario>

  <!-- MODAL DE AUDITORIA -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="JobConfig"
    :chave-primaria="jobSelecionadoParaAuditoria?.id"
    :titulo-registro="jobSelecionadoParaAuditoria?.nomeJob"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, onUnmounted } from 'vue'
import api from '@/services/api'
import { usePaginacao } from '@/composables/usePaginacao'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const filtrosAbertos = ref(false)

const modalHistoricoAberto = ref(false)
const jobSelecionado = ref(null)
const historicoLogs = ref([])
const loadingHistorico = ref(false)

const modalErroAberto = ref(false)
const logErroSelecionado = ref('')

const mostrarAuditoria = ref(false)
const jobSelecionadoParaAuditoria = ref(null)

let pollingTimer = null

const filtros = reactive({
  busca: '',
  statusUltimo: ''
})

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'id', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'identificacao', label: 'Identificação do Job', sortable: false, slotName: 'identificacao' },
  { field: 'expressaoCron', label: 'Frequência', align: 'center', sortable: false, slotName: 'frequencia' },
  { field: 'ultimosResultados', label: 'Últimas 5 Execuções', align: 'center', sortable: false, slotName: 'ultimasExecucoes' },
  { field: 'dataProximaExecucao', label: 'Próxima Execução (UTC)', sortable: true, slotName: 'proximaExecucao' },
  { field: 'isAtivo', label: 'Status Global', align: 'center', sortable: true, slotName: 'statusGlobal' },
  { field: 'acoes', label: 'Ações', width: 180, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const buscarDados = async (pageNumber = 1) => {
  loading.value = true
  try {
    const params = injetarParametros(pageNumber)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.statusUltimo) params.append('statusUltimo', filtros.statusUltimo)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/JobConfigs?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar configurações de Jobs.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const verificarJobsPendentes = async () => {
  const temPendente = lista.value.some(j =>
    j.executarImediatamente ||
    j.ultimoStatus === 'Em Andamento' ||
    (j.isAtivo && j.tentativasAtuais > 0) ||
    (j.isAtivo && j.dataProximaExecucao && new Date(j.dataProximaExecucao) <= new Date())
  )

  if (temPendente) {
    try {
      const params = injetarParametros(paginacao.value.CurrentPage || 1)
      if (filtros.busca) params.append('busca', filtros.busca)
      if (filtros.statusUltimo) params.append('statusUltimo', filtros.statusUltimo)
      if (sortField.value) params.append('sortField', sortField.value)
      if (sortOrder.value) params.append('sortOrder', sortOrder.value)

      const response = await api.get(`/JobConfigs?${params.toString()}`)
      lista.value = response.data
      atualizarPaginacao(response.headers)
    } catch (e) {}
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.statusUltimo = ''; buscarDados(1) }
const mudarPagina = (page) => { buscarDados(page) }
const mudarTamanhoPagina = (size) => { paginacao.value.PageSize = size; buscarDados(1) }

const novoItem = () => { emit('adicionar-item') }
const editar = (item) => { emit('editar-item', item) }

const executarAgora = async (job) => {
  if (job.executarImediatamente) return
  try {
    job.executarImediatamente = true
    await api.post(`/JobConfigs/${job.id}/executar-agora`)
    emit('show-notification', { message: `Sinal de execução enviado para "${job.nomeJob}".`, type: 'success' })
  } catch (error) {
    job.executarImediatamente = false
    emit('show-notification', { message: 'Erro ao enviar sinal de execução.', type: 'error' })
  }
}

const pararJob = async (job) => {
  try {
    job.isAtivo = false
    job.executarImediatamente = false
    await api.post(`/JobConfigs/${job.id}/stop`)
    emit('show-notification', { message: `Job "${job.nomeJob}" inativado com sucesso.`, type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao tentar parar o Job.', type: 'error' })
  }
}

const abrirAuditoria = (job) => {
  jobSelecionadoParaAuditoria.value = job
  mostrarAuditoria.value = true
}

const abrirHistorico = async (job) => {
  jobSelecionado.value = job
  historicoLogs.value = []
  loadingHistorico.value = true
  modalHistoricoAberto.value = true

  try {
    const res = await api.get(`/JobLogs?nomeJob=${job.nomeJob}&pageNumber=1&pageSize=15`)
    historicoLogs.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar histórico.', type: 'error' })
  } finally {
    loadingHistorico.value = false
  }
}

const verDetalheErro = (log) => {
  logErroSelecionado.value = log.mensagemErro ? `[ERRO TÉCNICO]\n\n${log.mensagemErro}` : `[MÉTRICAS DA EXECUÇÃO]\n\n${log.detalhesJson}`
  modalErroAberto.value = true
}

const obterClasseStatusLog = (status) => {
  if (status === 'Sucesso') return 'badge bg-success'
  if (status === 'Falha') return 'badge bg-danger'
  if (status === 'Em Andamento') return 'badge bg-warning text-dark'
  return 'badge bg-secondary'
}

const isAtrasado = (dataStr) => {
  if (!dataStr) return false
  return new Date(dataStr) < new Date()
}

onMounted(() => {
  buscarDados(1)
  pollingTimer = setInterval(verificarJobsPendentes, 5000)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

onUnmounted(() => {
  if (pollingTimer) clearInterval(pollingTimer)
})

defineExpose({ buscarDados })
</script>

<style scoped>
.acoes-cell { vertical-align: middle; padding-right: 1rem; }
.btn-icon { background: none; border: none; padding: 0.4rem; transition: 0.2s; font-size: 1.1rem; }
.btn-icon:hover { filter: brightness(0.8); }
.btn-icon:disabled { opacity: 0.3; cursor: not-allowed; }

.acoes-hover-container .btn-hover-only {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}
.admin-table tr:hover .acoes-hover-container .btn-hover-only {
  opacity: 1;
}
.acoes-hover-container .always-visible {
  opacity: 1;
}
</style>
